// src/components/ndr-management/NDRDetailsModal.jsx
import React from "react";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import { toZonedTime } from "date-fns-tz";

// NDR Status Code Mapping
const NDR_STATUS_MAP = {
  "0": "Unknown Exception",
  "1": "Customer Unavailable",
  "2": "Rejected by Customer",
  "3": "Delivery Rescheduled",
  "4": "No Attempt",
  "5": "Customer Unreachable",
  "6": "Address Issue",
  "7": "Payment Issue",
  "8": "Out Of Delivery Area",
  "9": "Order Already Cancelled",
  "10": "Self Collect",
  "11": "Shipment Seized By Customer",
  "12": "Customer wants open delivery",
  "13": "Shipment Misrouted by logistics partner",
  "14": "OTP-based cancellation",
  "15": "OTP mismatch or unavailable",
};

// NDR Management Status Mapping
const NDR_MANAGEMENT_STATUS_MAP = {
  "100": "Pending Action",
  "200": "Initiated Action",
  "300": "Closed",
  "9999": "Unknown Status",
};

// Action Code Mapping
const ACTION_CODE_MAP = {
  "100": "Reattempt",
  "200": "Initiate RTO",
};

export default function NDRDetailsModal({ ndr, onClose }) {
  // Convert UTC to IST for display with relative time (up to minutes)
  const formatDateWithRelative = (dateString) => {
    if (!dateString) return "—";
    try {
      const utcDate = parseISO(dateString);
      const istDate = toZonedTime(utcDate, 'Asia/Kolkata');
      const formatted = format(istDate, "dd MMM yyyy, HH:mm");
      const relative = formatDistanceToNow(istDate, { addSuffix: true });
      return (
        <div className="flex flex-col">
          <span className="font-medium">{formatted}</span>
          <span className="text-xs text-gray-500">{relative}</span>
        </div>
      );
    } catch {
      return dateString;
    }
  };

  // Convert UTC to IST for display (up to minutes)
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      const utcDate = parseISO(dateString);
      const istDate = toZonedTime(utcDate, 'Asia/Kolkata');
      return format(istDate, "dd MMM yyyy, HH:mm");
    } catch {
      return dateString;
    }
  };

  // Convert UTC to IST for display (date only, no time)
  const formatDateOnly = (dateString) => {
    if (!dateString) return "—";
    try {
      const utcDate = parseISO(dateString);
      const istDate = toZonedTime(utcDate, 'Asia/Kolkata');
      return format(istDate, "dd MMM yyyy");
    } catch {
      return dateString;
    }
  };

  const getNDRStatusLabel = (code) => {
    return NDR_STATUS_MAP[code] || code;
  };

  const getNDRManagementStatusLabel = (code) => {
    return NDR_MANAGEMENT_STATUS_MAP[code] || code;
  };

  const getActionLabel = (code) => {
    return ACTION_CODE_MAP[code] || code;
  };

  const getStatusBadge = (statusCode) => {
    const statusMap = {
      "100": "bg-yellow-100 text-yellow-800",
      "200": "bg-blue-100 text-blue-800",
      "300": "bg-gray-100 text-gray-800",
      "9999": "bg-red-100 text-red-800",
    };
    return statusMap[statusCode] || "bg-gray-100 text-gray-800";
  };

  // Format full address with all details
  const formatFullAddress = (actionDetails) => {
    if (!actionDetails) return "—";
    
    const parts = [];
    
    if (actionDetails.drop_address) parts.push(actionDetails.drop_address);
    if (actionDetails.drop_city) parts.push(actionDetails.drop_city);
    if (actionDetails.drop_state) parts.push(actionDetails.drop_state);
    if (actionDetails.drop_postal_code) parts.push(actionDetails.drop_postal_code);
    if (actionDetails.drop_country_code) parts.push(actionDetails.drop_country_code);
    
    return parts.length > 0 ? parts.join(", ") : "—";
  };

  // Format contact with name and number
  const formatContact = (actionDetails) => {
    if (!actionDetails) return "—";
    
    const parts = [];
    
    if (actionDetails.drop_poc) parts.push(actionDetails.drop_poc);
    if (actionDetails.drop_poc_contact_number) parts.push(actionDetails.drop_poc_contact_number);
    
    return parts.length > 0 ? parts.join(" - ") : "—";
  };

  // Sort actions in reverse chronological order (newest first)
  const getSortedActions = () => {
    if (!ndr.ndr_actions || ndr.ndr_actions.length === 0) return [];
    
    return [...ndr.ndr_actions].sort((a, b) => {
      const aDetails = a.reattempt_action_details || a.initiate_rto_action_details;
      const bDetails = b.reattempt_action_details || b.initiate_rto_action_details;
      
      const aDate = aDetails?.created_date_time;
      const bDate = bDetails?.created_date_time;
      
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      
      // Sort in descending order (newest first)
      return new Date(bDate) - new Date(aDate);
    });
  };

  const sortedActions = getSortedActions();

  const DetailRow = ({ label, value }) => (
    <div className="grid grid-cols-3 gap-4 py-2 border-b">
      <span className="font-medium text-gray-700">{label}:</span>
      <span className="col-span-2 text-gray-900">{value || "—"}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-[95vw] w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center z-10">
          <h2 className="text-2xl font-bold">NDR Details</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Order Information */}
          <div>
            <h3 className="text-lg font-semibold mb-3 text-blue-600">
              Order Information
            </h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <DetailRow label="Merchant ID" value={ndr.merchant_id} />
              <DetailRow
                label="Order Reference"
                value={ndr.order_reference_number}
              />
              <DetailRow label="AWB Number" value={ndr.awb_number} />
              <DetailRow
                label="Carrier Partner"
                value={ndr.carrier_partner_public_name}
              />
              <DetailRow label="Order Type" value={ndr.order_type} />
              <DetailRow
                label="Order Invoice Value"
                value={
                  ndr.order_invoice_value
                    ? `${ndr.order_invoice_currency || ""} ${ndr.order_invoice_value}`
                    : "—"
                }
              />
              <DetailRow
                label="Order Creation Date (IST)"
                value={formatDate(ndr.order_creation_date_time_utc)}
              />
            </div>
          </div>

          {/* NDR Status Information */}
          <div>
            <h3 className="text-lg font-semibold mb-3 text-blue-600">
              NDR Status
            </h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <DetailRow
                label="NDR Management Status"
                value={
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(
                      ndr.ndr_management_status_code
                    )}`}
                  >
                    {getNDRManagementStatusLabel(ndr.ndr_management_status_code)}
                  </span>
                }
              />
              <DetailRow
                label="Latest NDR Status"
                value={getNDRStatusLabel(ndr.latest_ndr_status_code)}
              />
              <DetailRow
                label="Latest NDR Description"
                value={ndr.latest_ndr_status_description}
              />
              <DetailRow
                label="Latest NDR Date (IST)"
                value={formatDate(ndr.latest_ndr_status_date_time_utc)}
              />
              <DetailRow
                label="NDR Creation Date (IST)"
                value={formatDate(ndr.ndr_creation_date_time_utc)}
              />
              <DetailRow
                label="Out for Delivery Attempts"
                value={ndr.out_for_delivery_attempts_count}
              />
            </div>
          </div>

          {/* Current Order Status */}
          <div>
            <h3 className="text-lg font-semibold mb-3 text-blue-600">
              Current Order Status
            </h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <DetailRow
                label="Description"
                value={ndr.current_order_status_description_external}
              />
              <DetailRow
                label="Current Location"
                value={ndr.current_order_status_current_location}
              />
              <DetailRow
                label="Remarks"
                value={ndr.current_order_status_remarks}
              />
            </div>
          </div>

          {/* Delivery Address */}
          {ndr.delivery_address && (
            <div>
              <h3 className="text-lg font-semibold mb-3 text-blue-600">
                Delivery Address
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <DetailRow
                  label="Contact Person"
                  value={ndr.delivery_address.drop_poc}
                />
                <DetailRow
                  label="Contact Number"
                  value={ndr.delivery_address.drop_poc_contact_number}
                />
                <DetailRow
                  label="Address"
                  value={ndr.delivery_address.drop_address}
                />
                <DetailRow
                  label="Landmark"
                  value={ndr.delivery_address.drop_landmark}
                />
                <DetailRow
                  label="City"
                  value={ndr.delivery_address.drop_city}
                />
                <DetailRow
                  label="State"
                  value={ndr.delivery_address.drop_state}
                />
                <DetailRow
                  label="Postal Code"
                  value={ndr.delivery_address.drop_postal_code}
                />
                <DetailRow
                  label="Country Code"
                  value={ndr.delivery_address.drop_country_code}
                />
              </div>
            </div>
          )}

          {/* NDR Actions Table */}
          {sortedActions.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3 text-blue-600">
                NDR Action History
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">
                        Action
                      </th>
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold text-gray-700">
                        Address
                      </th>
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">
                        Contact
                      </th>
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">
                        Preferred Delivery Date
                      </th>
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">
                        Action Taken By
                      </th>
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">
                        Action Date Time
                      </th>
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold text-gray-700">
                        Internal Remarks
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedActions.map((action, index) => {
                      // Get action details based on action type
                      const actionDetails = action.reattempt_action_details || action.initiate_rto_action_details;
                      const isReattempt = action.action === "100" && action.reattempt_action_details;
                      
                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-4 py-2 whitespace-nowrap">
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {getActionLabel(action.action)}
                            </span>
                          </td>
                          <td className="border border-gray-300 px-4 py-2 max-w-md">
                            <div className="text-sm">
                              {formatFullAddress(actionDetails)}
                            </div>
                          </td>
                          <td className="border border-gray-300 px-4 py-2 whitespace-nowrap">
                            <div className="text-sm">
                              {formatContact(actionDetails)}
                            </div>
                          </td>
                          <td className="border border-gray-300 px-4 py-2 whitespace-nowrap">
                            {isReattempt && actionDetails?.preferred_delivery_date
                              ? formatDateOnly(actionDetails.preferred_delivery_date)
                              : "—"}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 whitespace-nowrap">
                            {actionDetails?.created_by || "—"}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 whitespace-nowrap">
                            {formatDateWithRelative(actionDetails?.created_date_time)}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 max-w-xs">
                            <div className="text-sm" title={actionDetails?.internal_remarks || "—"}>
                              {actionDetails?.internal_remarks || "—"}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Close Button */}
        <div className="sticky bottom-0 bg-white border-t p-6">
          <button
            onClick={onClose}
            className="w-full px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}